/**
 * Messaging Service V2 - PostgreSQL Backend Only
 * ===============================================
 * Simple, secure messaging using the backend API.
 * No Supabase dependency - all data comes from PostgreSQL.
 */

import { API_URL } from '../config';
import { getAuthHeaders, hasCredentialReadyAuth } from './api';

// =============================================================================
// Types
// =============================================================================

export interface Message {
  id: number;
  request_id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  created_at: string;
  read: boolean;
  sender_name?: string;
}

export interface Conversation {
  request_id: number;
  guest_spot_title: string;
  shoot_title: string;
  shoot_date: string | null;
  shoot_id: number;
  other_user_name: string;
  other_user_id: string;
  has_messages: boolean;
  message_count: number;
  unread_count: number;
  latest_message: string;
  latest_message_time: string;
  status: string;
  can_send: boolean;
  is_creator?: boolean;
  creator_username?: string;
  archived?: boolean;
  archived_at?: string | null;
  // Optional fields for compatibility
  guest_stats?: {
    accepted_spots: number;
    rejected_spots: number;
    pending_spots: number;
    total_requested: number;
    accepted_spot_titles: string[];
    accepted_spots_details: { id: number; title: string }[];
  };
}

export interface ConversationDetails {
  request_id: number;
  guest_spot_id: number;
  guest_spot_title: string;
  shoot_id: number;
  shoot_title: string;
  shoot_date: string | null;
  creator_id: string;
  guest_id: string;
  other_user_id: string;
  other_user_username: string;
  other_user_email: string | null;
  other_user_profile_image: string | null;
  creator_username: string;
  guest_username: string;
  accepted_spots_details: { id: number; title: string }[];
  status: string;
  can_send: boolean;
  messages: Message[];
}

export class MessagingRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'MessagingRequestError';
    this.status = status;
  }
}

const SEVERE_ABUSE_MESSAGE =
  'This text contains targeted harassment, hateful language, or a violent threat. Please revise it.';

const readMessagingError = async (response: Response, fallback: string): Promise<string> => {
  const data = await response.json().catch(() => null);
  const detail = data?.detail;

  if (detail && typeof detail === 'object' && detail.code === 'SEVERE_ABUSE_BLOCKED') {
    return SEVERE_ABUSE_MESSAGE;
  }
  if (detail && typeof detail === 'object' && detail.code === 'CONVERSATION_READ_ONLY') {
    return detail.message || 'This conversation is read-only because the request is no longer active.';
  }

  if (response.status === 401) return 'Please sign in again to continue.';
  if (response.status === 403) return 'You do not have access to this conversation.';
  if (response.status === 404) return 'This conversation is no longer available.';
  if (response.status === 409) return 'This conversation changed. Refresh and try again.';
  if (response.status === 429) return 'Too many attempts. Please wait a moment and try again.';
  return fallback;
};

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get all available conversations for the current user
 */
export async function getAvailableConversations(
  userId: string,
  includeArchived = false
): Promise<Conversation[]> {
  const query = includeArchived ? '?include_archived=true' : '';
  const response = await fetch(`${API_URL}/api/messages/available${query}`, {
    headers: { ...getAuthHeaders() },
  });
  
  if (!response.ok) {
    throw new Error('Failed to load conversations');
  }
  
  return response.json();
}

/**
 * Get a conversation with all messages
 */
export async function getConversation(requestId: number, userId: string): Promise<ConversationDetails> {
  const response = await fetch(`${API_URL}/api/messages/conversation/${requestId}`, {
    headers: { ...getAuthHeaders() },
  });
  
  if (!response.ok) {
    throw new Error(await readMessagingError(response, 'Failed to load conversation'));
  }
  
  return response.json();
}

/**
 * Send a message
 */
export async function sendMessage(
  requestId: number,
  message: string,
  userId: string
): Promise<{ id: number; message: string; created_at: string }> {
  const response = await fetch(`${API_URL}/api/messages/conversation/${requestId}/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ message }),
  });
  
  if (!response.ok) {
    throw new Error(await readMessagingError(response, 'Message could not be sent. Please review it and try again.'));
  }
  
  return response.json();
}

/**
 * Get messages only (for polling updates)
 */
export async function getMessages(
  requestId: number,
  userId: string,
  since?: string
): Promise<Message[]> {
  const url = new URL(`${API_URL}/api/messages/conversation/${requestId}/messages`);
  if (since) {
    url.searchParams.set('since', since);
  }
  
  const response = await fetch(url.toString(), {
    headers: { ...getAuthHeaders() },
  });
  
  if (!response.ok) {
    throw new Error('Failed to load messages');
  }
  
  return response.json();
}

/**
 * Get total unread message count
 */
export async function getUnreadCount(userId: string): Promise<number> {
  if (!hasCredentialReadyAuth()) {
    throw new MessagingRequestError('Authentication is not ready', 401);
  }

  const response = await fetch(`${API_URL}/api/messages/unread-count`, {
    headers: { ...getAuthHeaders() },
  });
  
  if (!response.ok) {
    throw new MessagingRequestError('Failed to load unread message count', response.status);
  }
  
  const data = await response.json();
  return data.unread_count || 0;
}

/**
 * Archive a conversation for the current user without removing shared messages.
 */
export async function archiveConversation(
  requestId: number,
  userId: string
): Promise<{ message: string; archived: boolean; archived_at: string | null }> {
  const response = await fetch(`${API_URL}/api/messages/conversation/${requestId}/archive`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },
  });
  
  if (!response.ok) {
    throw new Error(await readMessagingError(response, 'Failed to archive conversation'));
  }

  return response.json();
}

/** Restore an archived conversation for the current user. */
export async function restoreConversation(
  requestId: number,
  userId: string
): Promise<{ message: string; archived: boolean; archived_at: null }> {
  const response = await fetch(`${API_URL}/api/messages/conversation/${requestId}/restore`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },
  });

  if (!response.ok) {
    throw new Error(await readMessagingError(response, 'Failed to restore conversation'));
  }

  return response.json();
}

/** Star or unstar a creator-side conversation. */
export async function toggleConversationStar(
  requestId: number
): Promise<{ is_starred: boolean }> {
  const response = await fetch(`${API_URL}/api/requests/${requestId}/star`, {
    method: 'POST',
    headers: { ...getAuthHeaders() },
  });

  if (!response.ok) {
    throw new Error(await readMessagingError(response, 'The conversation could not be updated.'));
  }

  return response.json();
}

/** @deprecated Use archiveConversation. The backend DELETE route also archives. */
export const deleteConversation = archiveConversation;
